import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

export const pricingAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: true, max: 80 },
  { name: 'subheadline', type: 'string', required: false, max: 200 },
  {
    name: 'plans',
    type: 'array<object>',
    required: true,
    fields: [
      { name: 'name', type: 'string', required: true, max: 30 },
      { name: 'price', type: 'string', required: true, max: 20, hint: 'e.g. "$29/mo" or "Free"' },
      { name: 'period', type: 'string', required: false, max: 20 },
      { name: 'description', type: 'string', required: false, max: 120 },
      { name: 'highlighted', type: 'string', required: false, max: 4, hint: '"true" if popular' },
      { name: 'badge', type: 'string', required: false, max: 20, hint: 'おすすめ表示時のバッジ文言 (省略時は "Most popular"、空にすると非表示)' },
      { name: 'ctaLabel', type: 'string', required: true, max: 24 },
      { name: 'ctaHref', type: 'url', required: false },
      {
        name: 'features',
        type: 'array<string>',
        required: true,
      },
    ],
  },
];

export const pricingUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'Eyebrow', max: 30 },
  { name: 'headline', type: 'text', label: 'Headline', required: true, max: 80 },
  { name: 'subheadline', type: 'textarea', label: 'Subheadline', max: 200 },
  { name: 'plans', type: 'plan_list', label: 'Plans', required: true },
];

export const pricingZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().min(1).max(80),
  subheadline: z.string().max(200).optional().nullable(),
  plans: z
    .array(
      z.object({
        name: z.string().max(30),
        price: z.string().max(20),
        period: z.string().max(20).optional().nullable(),
        description: z.string().max(120).optional().nullable(),
        highlighted: z
          .union([z.boolean(), z.string()])
          .optional()
          .nullable()
          .transform((v) => v === true || v === 'true'),
        badge: z.string().max(20).optional().nullable(),
        ctaLabel: z.string().max(24),
        ctaHref: z.string().optional().nullable(),
        features: z.array(z.string().max(120)).min(1),
      }),
    )
    .min(1)
    .max(4),
});

export type PricingContent = z.infer<typeof pricingZodSchema>;

export const pricingSample: PricingContent = {
  eyebrow: 'Pricing',
  headline: 'Simple plans that grow with you.',
  subheadline: '14-day free trial. No credit card required.',
  plans: [
    {
      name: 'Starter',
      price: '$0',
      period: '/mo',
      description: 'For solo makers',
      highlighted: false,
      ctaLabel: 'Start free',
      ctaHref: '#',
      features: ['1 project', 'Up to 3 collaborators', 'Community support'],
    },
    {
      name: 'Pro',
      price: '$29',
      period: '/mo',
      description: 'For growing teams',
      highlighted: true,
      ctaLabel: 'Start trial',
      ctaHref: '#',
      features: [
        'Unlimited projects',
        'Up to 25 collaborators',
        'Priority support',
        'Advanced analytics',
      ],
    },
    {
      name: 'Studio',
      price: 'Custom',
      period: '',
      description: 'For studios and enterprises',
      highlighted: false,
      ctaLabel: 'Contact sales',
      ctaHref: '#',
      features: [
        'Custom seat counts',
        'SSO + SCIM',
        'Dedicated success manager',
        'Custom contracts',
      ],
    },
  ],
};
